import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { BlogCard } from "@/components/BlogCard";
import { Newsletter } from "@/components/Newsletter";
import { AdSlot, AdDisclaimer } from "@/components/AdSlot";
import { ShareButtons } from "@/components/ShareButtons";
import { Clock, User } from "lucide-react";

const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/blog/posts/${slug}`)
      .then((r) => {
        setPost(r.data.post);
        setRelated(r.data.related);
        document.title = `${r.data.post.title} — Xurray Journal`;
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return <Layout hideAd><main className="max-w-2xl mx-auto px-4 py-24 text-center"><h1 className="font-display font-bold text-2xl">Article not found</h1><Link to="/blog" className="text-primary underline mt-4 inline-block">Back to Xurray Journal</Link></main></Layout>;
  }
  if (!post) return <Layout hideAd><main className="max-w-2xl mx-auto px-4 py-24 text-center text-muted-foreground">Loading…</main></Layout>;

  const renderBlocks = () => {
    const out = [];
    let paraCount = 0;
    let adInserted = false;
    post.content.forEach((b, i) => {
      if (b.type === "h2") {
        out.push(<h2 key={i} className="font-display font-bold text-2xl mt-8 mb-3">{b.text}</h2>);
      } else {
        paraCount += 1;
        out.push(<p key={i} className="mb-4 leading-relaxed text-[17px] text-foreground/90">{b.text}</p>);
        if (paraCount === 2 && !adInserted) {
          adInserted = true;
          out.push(<AdSlot key="incontent-ad" placement="incontent" />);
        }
      }
    });
    return out;
  };

  return (
    <Layout hideAd>
      <main className="max-w-6xl mx-auto px-4 py-8">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-primary">Home</Link> <span aria-hidden="true">/</span>{" "}
          <Link to="/blog" className="hover:text-primary">Journal</Link> <span aria-hidden="true">/</span>{" "}
          <span className="text-foreground">{post.category}</span>
        </nav>

        <div className="grid lg:grid-cols-[1fr_320px] gap-10">
          <article data-testid="blog-article">
            <header>
              <span className="inline-block text-xs font-semibold text-primary bg-primary/10 rounded-full px-3 py-1">{post.category}</span>
              <h1 className="font-display font-bold text-3xl sm:text-4xl leading-tight mt-4">{post.title}</h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground mt-4">
                <span className="flex items-center gap-1.5"><User className="w-4 h-4" aria-hidden="true" />{post.author}</span>
                <span aria-hidden="true">·</span>
                <time dateTime={post.published_at}>{fmtDate(post.published_at)}</time>
                <span aria-hidden="true">·</span>
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" aria-hidden="true" />{post.reading_time} min read</span>
              </div>
            </header>

            <figure className="my-6">
              <img src={post.cover_image} alt={post.title} width="1200" height="675" className="w-full rounded-2xl object-cover aspect-[16/9]" fetchpriority="high" />
            </figure>

            <div className="article-body font-serif">
              {renderBlocks()}
            </div>

            {/* Social share */}
            <ShareButtons title={post.title} className="mt-8 pt-6 border-t border-border" />
          </article>

          <aside className="space-y-8" aria-label="Sidebar">
            <Newsletter compact />
            <AdSlot placement="sidebar" />
            <AdDisclaimer />
          </aside>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section aria-label="Related articles" className="mt-14">
            <h2 className="font-display font-bold text-2xl mb-5">Related Articles</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {related.map((p) => <BlogCard key={p.slug} post={p} />)}
            </div>
          </section>
        )}

        <AdSlot placement="footer" className="mt-12" />
      </main>
    </Layout>
  );
}
